/* eslint-disable jsx-a11y/alt-text */
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import Slider from "react-slick"
import "slick-carousel/slick/slick-theme.css"
import "slick-carousel/slick/slick.css"
import { StepCategory } from '../../../components/Container/Onboarding/utils'
import Image from '../../../components/Presentational/Image'
import { PortalAccountDomain } from '../../../utils/enums'
import { useAppState } from '../../../utils/hooks'
import Routes from '../../../utils/routes'
import { useThemes } from '../../../utils/themes'
import styles from './Banner.module.scss'
import image1 from './images/carousel/1.png'
import image2 from './images/carousel/2.png'
import image3 from './images/carousel/3.png'
import image4 from './images/carousel/4.png'
import image5 from './images/carousel/5.png'
import image6 from './images/carousel/6.png'
import image7 from './images/carousel/7.png'
import shieldLight from './images/shield-light.png'
import shieldDark from './images/shield-dark.png'
import lockpadLight from './images/lockpad-light.png'
import lockpadDark from './images/lockpad-dark.png'
import globe from './images/globe.png'
import globeDark from './images/globe-dark.png'
import chartLight from './images/chart-light.png'
import chartDark from './images/chart-dark.png'
import { ReactComponent as Icon1 } from './images/icons/withdrawl.svg'
import { ReactComponent as Icon2 } from './images/icons/currency.svg'
import { ReactComponent as Icon3 } from './images/icons/traders gym.svg'
import { ReactComponent as Icon4 } from './images/icons/award winning.svg'
import { ReactComponent as DemoIcon1 } from './images/icons/clock-rotate-right.svg'
import { ReactComponent as DemoIcon2 } from './images/icons/candlestick-chart.svg'
import { ReactComponent as DemoIcon3 } from './images/icons/learning.svg'
import { ReactComponent as DemoIcon4 } from './images/icons/coins-swap.svg'
import { ReactComponent as DocsPhotoId } from './images/icons/kyc-icon-photo-id.svg'
import { ReactComponent as DocsAddressProof } from './images/icons/kyc-icon-address-proof.svg'



const sliderSettings = {
	arrows: false,
	fade: true,
	dots: true,
	infinite: true,
	speed: 500,
	slidesToShow: 1,
	slidesToScroll: 1,
	// dotsClass: styles.dots,
}

const banners = [
	{ number: 1, image: image1 },
	{ number: 2, image: image2 },
	{ number: 3, image: image3 },
	{ number: 4, image: image4 },
	{ number: 5, image: image5 },
	{ number: 6, image: image6 },
	{ number: 7, image: image7 }
]

const CarouselImage = ({ number, image }: { number: number, image: string }) => {
	const { t } = useTranslation("banners")
	return <div className={styles.carouselImage}>
		<img src={image} />
		<div className={styles.title}>{t(`carousel.${number}.title`)}</div>
		<div className={styles.text}>{t(`carousel.${number}.text`)}</div>
	</div>
}

type GenericVariant = 'default' | 'demo' | 'documents' | 'partner'

const genericVariantConfig: Record<GenericVariant, { keyPrefix: string; icons: JSX.Element[]; showText: boolean }> = {
	default: {
		keyPrefix: 'login',
		icons: [<Icon1 />, <Icon2 />, <Icon3 />, <Icon4 />],
		showText: true,
	},
	demo: {
		keyPrefix: 'demo',
		icons: [<DemoIcon1 />, <DemoIcon2 />, <DemoIcon3 />, <DemoIcon4 />],
		showText: false,
	},
	documents: {
		keyPrefix: 'personal.documentsReady',
		icons: [<DocsPhotoId />, <DocsAddressProof />],
		showText: true,
	},
	partner: {
		keyPrefix: 'partner',
		icons: [],
		showText: true,
	},
}

const PARTNER_ICON_NAMES = ['trusted', 'comission', 'daily-payouts', 'realtime-dashboard']

const GenericBanner = ({ variant = 'default' }: { variant?: GenericVariant }) => {
	const { t } = useTranslation("banners")
	const { theme } = useThemes()
	const { keyPrefix: ls, icons: configIcons, showText } = genericVariantConfig[variant]

	const icons = useMemo(() => (
		variant === 'partner'
			? PARTNER_ICON_NAMES.map(name => (
				<img src={`${process.env.PUBLIC_URL}/images/sidebar/${name}-${theme === 'dark' ? 'dark' : 'light'}.svg`} alt="" />
			))
			: configIcons
	), [variant, theme, configIcons])

	const items = useMemo(() => icons.map((icon, i) => ({
		icon,
		title: t(`${ls}.${i + 1}.title`),
		content: t(`${ls}.${i + 1}.text`),
	})), [t, ls, icons])

	const variantClass = variant === 'demo' ? styles.demo : variant === 'documents' ? styles.documents : variant === 'partner' ? styles.partner : ''

	return <div className={`${styles.generic}${variantClass ? ' ' + variantClass : ''}`}>
		<div className={styles.title}>{t(`${ls}.title`)}</div>
		{showText && <div className={styles.text}>{t(`${ls}.text`)}</div>}
		<div className={styles.promo}>
			{items.map((item, index) => (
				<div key={index} className={styles.item}>
					<div className={styles.icon}>{item.icon}</div>
					<div>
						<div className={styles.title}>{item.title}</div>
						<div className={styles.text}>{item.content}</div>
					</div>
				</div>
			))}
		</div>
	</div>
}

/* const GenericBanner = () => {
	return (
		<div className={styles.common}>
			<Slider {...sliderSettings}				>
				{[1, 3, 5].map((number) => (
					<CarouselImage key={number} number={number} image={banners[number - 1].image} />)
				)}
			</Slider>
		</div>
	)
}
 */
const OrgBanner = () => {
	const { portalAccountDomain, onboardingDomain } = useAppState()
	const orgBanners: Partial<Record<PortalAccountDomain, number[]>> = {
		AU: [2, 3, 1],
		TMNZ: [3, 2, 1],
		UK: [2, 3, 1],
		TMEU: [3, 7, 1],
		TFSA: [6, 3, 1],
		TMSY: [4, 2, 3, 7],
		TMLC: [7, 2, 3, 1],
	}
	const domain = (onboardingDomain || portalAccountDomain)
	const slides: Array<number> = domain && orgBanners[domain]
		? orgBanners[domain]!
		: [3, 5, 1]
	return (
		<div className={styles.common}>
			<Slider {...sliderSettings}				>
				{slides.map((number) => (
					<CarouselImage key={number} number={number} image={banners[number - 1].image} />)
				)}
			</Slider>
		</div>
	)
}

const OnboardingBanner = ({ category }: { category: StepCategory }) => {
	const { t } = useTranslation("banners")
	const { theme } = useThemes()
	const { onboardingCountry: userCountry, onboardingDomain, portalAccountDomain } = useAppState()

	let image: string
	switch (category) {
		case 'employment':
		case 'income':
		case 'experience':
			image = theme === 'dark' ? shieldDark : shieldLight
			break
		case 'education':
			image = theme === 'dark' ? chartDark : chartLight
			break
		case 'assessment':
		case 'knowledge':
		case 'risk_assessment':
		case 'terms':
			image = theme === 'dark' ? lockpadDark : lockpadLight
			break
		case 'id_verification':
		case 'address_verification':
			image = theme === 'dark' ? globeDark : globe
			break
		default:
			image = theme === 'dark' ? shieldDark : shieldLight
	}

	const isUKResident = userCountry?.code2 === 'GB'
	// The pooled knowledge test ('knowledge') reuses the assessment left-panel
	// title/text — only the top TMD banner copy differs (THPRT-6275).
	const contentKey = (category === 'tax' && isUKResident)
		? 'taxUK'
		: category === 'knowledge' ? 'assessment' : category

	// THPRT-6262: the TMCY/TMEU (EU) entity uses "Appropriateness Test" wording on
	// the assessment and risk-warning banners (MiFID II non-advised service), while
	// AU/UK keep the shared "Suitability assessment" title. Only the title swaps; the
	// body text and image are reused from the original category.
	const isCYEntity = [PortalAccountDomain.TMCY, PortalAccountDomain.TMEU]
		.includes((onboardingDomain || portalAccountDomain) as PortalAccountDomain)
	const title = (isCYEntity && (category === 'assessment' || category === 'refer'))
		? t('appropriatenessTitle')
		: t(`${contentKey}.title`)

	const logoImage = theme === 'dark' ? 'tmLogo' : 'tmLogoLight'
	return (
		<div className={styles.onboarding}>
			<div className={styles.header}>
				<Image name={logoImage} className={styles.logo} />
				<div className={styles.title}>{title}</div>
				<div className={styles.text}>{t(`${contentKey}.text`)}</div>
			</div>
			<div className={styles.footer}>
				<img src={image} alt="" />
			</div>
		</div>
	)
}


const Banner = ({ category }: { category?: StepCategory }) => {
	const location = useLocation()
	const { portalAccountDomain, onboardingDomain } = useAppState()

	//THPRT-5778
	const showOrgBanner = false//[Routes.account.personalInformation].includes(location.pathname)
	const showOnboardingBanner = [Routes.onboarding].includes(location.pathname)
	const isDemo = [Routes.account.createAccount.demo].includes(location.pathname)
	const isPersonalInfo = [Routes.account.personalInformation].includes(location.pathname)
	const isPartner = [Routes.register.ib].includes(location.pathname)

	// TMLC keeps the previous/default banner; all other entities get the documents banner.
	const isTMLC = (onboardingDomain || portalAccountDomain) === PortalAccountDomain.TMLC

	const genericVariant: GenericVariant = isPartner ? 'partner' : (isPersonalInfo && !isTMLC) ? 'documents' : isDemo ? 'demo' : 'default'

	return (
		<div className={styles.banner}>
			{showOrgBanner
				? <OrgBanner />
				: showOnboardingBanner
					? <OnboardingBanner category={category || 'generic'} />
					: <GenericBanner variant={genericVariant} />
			}
		</div>
	)
}

export default Banner
